import { getItemFromStorage, removeItemFromStorage, saveItemToStorage } from './storage.lib';

const booleanSerializer = (value: boolean) => String(value);
const booleanDeserializer = (value: string): boolean => value === 'true';

const numberSerializer = (value: number) => String(value);
const numberDeserializer = (value: string) => Number(value);

const dateSerializer = (value: Date) => value.toISOString();
const dateDeserializer = (value: string) => new Date(value);

export const createNumberStorageItem = (key: string, options?: { storage?: Storage }) => {
  return new StorageItem<number>(key, {
    serializer: numberSerializer,
    deserializer: numberDeserializer,
    ...options,
  });
};

export const createBooleanStorageItem = (key: string, options?: { storage?: Storage }) => {
  return new StorageItem<boolean>(key, {
    serializer: booleanSerializer,
    deserializer: booleanDeserializer,
    ...options,
  });
};

export const createDateStorageItem = (key: string, options?: { storage?: Storage }) => {
  return new StorageItem<Date>(key, {
    serializer: dateSerializer,
    deserializer: dateDeserializer,
    ...options,
  });
};

export const createObjectStorageItem = <T = Record<string, unknown>>(
  key: string,
  options?: { serializer: (value: T) => string; deserializer: (value: string) => T; storage?: Storage },
) => {
  return new StorageItem<T>(
    key,
    options || {
      serializer: JSON.stringify,
      deserializer: JSON.parse,
    },
  );
};

export class StorageItem<T = string> {
  #key: string;
  #serializer: (value: T) => string;
  #deserializer: ((value: string) => T) | undefined;
  #storage: Storage;

  constructor(
    key: string,
    {
      serializer,
      deserializer,
      storage,
    }: { serializer?: (value: T) => string; deserializer?: (value: string) => T; storage?: Storage } = {},
  ) {
    this.#key = key;
    this.#serializer = serializer || JSON.stringify;
    this.#storage = storage || localStorage;

    if (deserializer) {
      this.#deserializer = deserializer;
    }
  }

  get key() {
    return this.#key;
  }

  get(): T | null {
    const value = getItemFromStorage(this.#key, this.#storage);

    if (value === null) return null;

    return this.#deserializer ? this.#deserializer(value) : (value as T);
  }

  set(value: T) {
    return saveItemToStorage(this.#key, typeof value === 'string' ? value : this.#serializer(value), this.#storage);
  }

  remove() {
    return removeItemFromStorage(this.#key, this.#storage);
  }
}
